import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export const Login: React.FC = () => {
  type Mode = 'login' | 'requestReset' | 'confirmReset';

  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, requestPasswordReset, confirmPasswordReset } = useAuth();
  const navigate = useNavigate();

  const resetUiState = () => {
    setError('');
    setInfoMessage('');
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetUiState();
    setIsLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.requiresPasswordChange) {
        navigate('/change-password', { state: { email, tempPassword: password } });
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetUiState();

    setIsLoading(true);
    try {
      await requestPasswordReset(email);
      setInfoMessage('If an account exists for that email, you will receive a verification code.');
      setMode('confirmReset');
    } catch (err: any) {
      setError(err.message || 'Could not request password reset');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetUiState();

    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);
    try {
      await confirmPasswordReset(email, resetCode, newPassword);
      setInfoMessage('Password reset successfully. Please sign in with your new password.');
      setPassword('');
      setResetCode('');
      setNewPassword('');
      setConfirmNewPassword('');
      setMode('login');
    } catch (err: any) {
      setError(err.message || 'Password reset failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 sm:space-y-8">
        <div>
          <img
            className="mx-auto h-12 sm:h-16 w-auto"
            src="https://d21tw07c6rnmp0.cloudfront.net/media/uploads/499/themesettings/2024/11/499_f98f5bb9f55742ad9d12f025c51d742e.png"
            alt="WMPG"
          />
          <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            WMPG Training Hub
          </p>
        </div>
        
        <form
          className="mt-6 sm:mt-8 space-y-6"
          onSubmit={
            mode === 'login'
              ? handleLoginSubmit
              : mode === 'requestReset'
                ? handleRequestResetSubmit
                : handleConfirmResetSubmit
          }
        >
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          {infoMessage && (
            <div className="rounded-md bg-blue-50 p-4">
              <div className="text-sm text-blue-800">{infoMessage}</div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base"
                placeholder="Email address"
              />
            </div>

            {mode === 'confirmReset' && (
              <div>
                <label htmlFor="reset-code" className="block text-sm font-medium text-gray-700 mb-1">
                  Verification code
                </label>
                <input
                  id="reset-code"
                  name="reset-code"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="Enter the code from your email"
                />
              </div>
            )}

            {mode === 'confirmReset' && (
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                  New password
                </label>
                <input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="At least 8 characters"
                />
              </div>
            )}

            {mode === 'confirmReset' && (
              <div>
                <label htmlFor="confirm-new-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm new password
                </label>
                <input
                  id="confirm-new-password"
                  name="confirm-new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="Re-enter new password"
                />
              </div>
            )}

            {mode === 'login' && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="Password"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            {mode === 'login' ? (
              <button
                type="button"
                onClick={() => {
                  resetUiState();
                  setMode('requestReset');
                }}
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                Forgot password?
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  resetUiState();
                  setMode('login');
                }}
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                Back to sign in
              </button>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation min-h-[48px] transition-colors"
            >
              {mode === 'login'
                ? (isLoading ? 'Signing in...' : 'Sign in')
                : mode === 'requestReset'
                  ? (isLoading ? 'Sending...' : 'Send reset code')
                  : (isLoading ? 'Resetting...' : 'Reset password')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
